import { useMemo } from 'react';

import { useFieldArray, useFormContext, Controller, useWatch } from 'react-hook-form';
import { FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import styled from 'styled-components';

import { PermissionGroupApp, useGetPermissionGroupsQuery } from 'data/__generated__';

import { TotemLabel } from 'components/TotemLabel';
import { Option, TotemSelect } from 'components/TotemSelect';
import { colors } from 'constants/colors';
import { TotemOrganizationSelect } from 'components/Select/TotemOrganizationSelect';

type PermissionField = {
    totemId: string | null;
    totemFullname: string | null;
    userGroups: string[];
};

/**
 * Les groupes proposés viennent de l'API (groupes du code ET groupes composés dans la page « Groupes de
 * permissions ») : aucune liste en dur. Un groupe totadmin ne s'affecte qu'en global — ses droits ne
 * sont pas scopés par totem, et l'API le refuse de toute façon.
 */
function useGroupOptions() {
    const { data } = useGetPermissionGroupsQuery();

    return useMemo(() => {
        const groups = data?.permissionGroups ?? [];
        const toOption = ({ slug, label }: { slug: string; label: string }): Option<string> => ({
            value: slug,
            label,
        });

        return {
            globalOptions: groups.map(toOption),
            totemOptions: groups.filter(({ app }) => app !== PermissionGroupApp.Totadmin).map(toOption),
            totopsSlugs: new Set(groups.filter(({ app }) => app === PermissionGroupApp.Totops).map(({ slug }) => slug)),
            labelOf: (slug: string) => groups.find((group) => group.slug === slug)?.label ?? slug,
        };
    }, [data]);
}

export const PermissionsEditor = ({ disabled = false }: { disabled?: boolean }) => {
    const { control } = useFormContext<{ permissions: PermissionField[] }>();
    const { globalOptions, totemOptions, totopsSlugs, labelOf } = useGroupOptions();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'permissions',
    });

    const usedTotemIds = fields.filter((f) => f.totemId != null).map((f) => f.totemId!);

    const watchedPermissions = useWatch({ control, name: 'permissions' });

    const globalIndex = fields.findIndex((f) => f.totemId == null);
    const globalGroups: string[] = watchedPermissions?.[globalIndex]?.userGroups ?? [];
    const siteFields = fields.map((field, index) => ({ field, index })).filter(({ field }) => field.totemId != null);

    return (
        <Container>
            <Section>
                <TotemLabel>Permissions globales (sur tous les totems)</TotemLabel>
                <Controller
                    name={`permissions.${globalIndex}.userGroups`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TotemSelect
                            isMulti
                            disabled={disabled}
                            value={value ?? []}
                            onChange={(selected) => onChange(selected)}
                            options={globalOptions}
                            placeholder="Groupes globaux"
                        />
                    )}
                />
            </Section>
            <Section>
                <TotemLabel>Permissions par totem</TotemLabel>
                {siteFields.map(({ field, index }) => {
                    const currentGroups: string[] = watchedPermissions?.[index]?.userGroups ?? [];
                    const duplicates = currentGroups.filter((g) => globalGroups.includes(g) && totopsSlugs.has(g));
                    const duplicateLabels = duplicates.map(labelOf);

                    return (
                        <PermissionRowContainer key={field.id}>
                            <PermissionRow>
                                <PermissionFields>
                                    <TotemNameLabel title={field.totemFullname ?? field.totemId ?? ''}>
                                        {field.totemFullname ?? field.totemId}
                                    </TotemNameLabel>
                                    <Controller
                                        name={`permissions.${index}.userGroups`}
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TotemSelect
                                                isMulti
                                                disabled={disabled}
                                                value={value ?? []}
                                                onChange={(selected) => onChange(selected)}
                                                options={totemOptions}
                                                placeholder="Groupes"
                                            />
                                        )}
                                    />
                                </PermissionFields>
                                {!disabled && (
                                    <RemoveButton type="button" onClick={() => remove(index)}>
                                        <FaTrash size={13} />
                                    </RemoveButton>
                                )}
                            </PermissionRow>
                            {duplicates.length > 0 && (
                                <WarningMessage>
                                    <FaExclamationTriangle size={12} />
                                    Les autorisations {duplicateLabels.join(', ')} ont déjà accordé(s) globalement, et
                                    sont donc appliquées sur tous les totems. La configuration par totem n'aura pas
                                    d'effet ici.
                                </WarningMessage>
                            )}
                        </PermissionRowContainer>
                    );
                })}
                {!disabled && (
                    <AddTotemContainer>
                        <TotemOrganizationSelect
                            value={null}
                            onChange={(totem) => {
                                if (totem) {
                                    append({ totemId: totem._id, totemFullname: totem.fullname, userGroups: [] });
                                }
                            }}
                            excludedTotemIds={usedTotemIds}
                        />
                    </AddTotemContainer>
                )}
            </Section>
        </Container>
    );
};

const Container = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

const PermissionRowContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PermissionRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const PermissionFields = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 4px;
`;

const WarningMessage = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: ${colors.orange};
`;

const TotemNameLabel = styled.span`
    font-weight: bold;
    color: ${colors.black};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${colors.pureBlack};
    padding: 5px;
    margin-top: 22px;

    &:hover {
        opacity: 0.7;
    }
`;

const AddTotemContainer = styled.div`
    margin-top: 10px;
`;
